import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getMyOrders, getProducts, type MyOrder } from '../services/api';
import type { UseCartReturn } from '../hooks/useCart';

interface OrderHistoryProps {
  cart: UseCartReturn;
}

export default function OrderHistory({ cart }: OrderHistoryProps) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<MyOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const [reorderMessage, setReorderMessage] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);

  const handleReorder = async (order: MyOrder) => {
    setReorderingId(order.id);
    setReorderMessage(null);
    try {
      const productsResponse = await getProducts();
      const allProducts = productsResponse.products;

      // Build a map of variantId -> { product, variant } for quick lookup
      const variantMap = new Map<string, { product: typeof allProducts[0]; variant: typeof allProducts[0]['variants'][0] }>();
      for (const product of allProducts) {
        for (const variant of product.variants) {
          variantMap.set(variant.id, { product, variant });
        }
      }

      const added: string[] = [];
      const skipped: string[] = [];

      for (const item of order.lineItems) {
        if (!item.variantId) {
          skipped.push(item.title);
          continue;
        }
        const match = variantMap.get(item.variantId);
        if (!match) {
          skipped.push(item.title);
          continue;
        }
        // Clamp to available inventory
        const qty = Math.min(item.quantity, match.variant.inventoryQuantity);
        if (qty > 0) {
          cart.addItem(match.product, match.variant, qty);
          added.push(item.title);
        } else {
          skipped.push(`${item.title} (out of stock)`);
        }
      }

      if (added.length > 0 && skipped.length === 0) {
        setReorderMessage({ type: 'success', text: `${added.length} item${added.length > 1 ? 's' : ''} added to cart.` });
      } else if (added.length > 0 && skipped.length > 0) {
        setReorderMessage({ type: 'warning', text: `${added.length} item${added.length > 1 ? 's' : ''} added. Skipped: ${skipped.join(', ')}` });
      } else {
        setReorderMessage({ type: 'error', text: `Could not reorder — all items are unavailable: ${skipped.join(', ')}` });
      }

      if (added.length > 0) {
        // Brief delay to let user see the message, then navigate to catalog
        setTimeout(() => navigate('/'), 1500);
      }
    } catch (err) {
      setReorderMessage({ type: 'error', text: 'Failed to load product catalog. Please try again.' });
    } finally {
      setReorderingId(null);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMyOrders();
      setOrders(data.orders);
      setHasMore(data.hasMore);
      setEndCursor(data.endCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!endCursor || loadingMore) return;
    try {
      setLoadingMore(true);
      const data = await getMyOrders(endCursor);
      setOrders((prev) => [...prev, ...data.orders]);
      setHasMore(data.hasMore);
      setEndCursor(data.endCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more orders');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const statusConfig: Record<string, { label: string; className: string }> = {
    OPEN: { label: 'Open', className: 'bg-amber-900/40 text-amber-300' },
    INVOICE_SENT: { label: 'Invoice Sent', className: 'bg-blue-900/40 text-blue-300' },
    COMPLETED: { label: 'Completed', className: 'bg-green-900/40 text-green-300' },
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-12 h-12 border-4 border-workshop-elevated border-t-accent rounded-full mx-auto"
          />
          <p className="mt-4 text-text-muted uppercase tracking-wide text-sm">
            Loading order history...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-900/30 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-display text-cream mb-2">Unable to Load Orders</h2>
        <p className="text-text-muted mb-6">{error}</p>
        <button onClick={fetchOrders} className="btn-primary">
          Try Again
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-display text-cream">My Orders</h1>
        <p className="text-text-muted mt-1">View your wholesale order history and invoices</p>
      </div>

      {/* Reorder Message */}
      {reorderMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 p-3 rounded-sm text-sm flex items-center justify-between ${
            reorderMessage.type === 'success' ? 'bg-green-900/30 border border-green-700/30 text-green-300' :
            reorderMessage.type === 'warning' ? 'bg-amber-900/30 border border-amber-700/30 text-amber-300' :
            'bg-red-900/30 border border-red-700/30 text-red-300'
          }`}
        >
          <p>{reorderMessage.text}</p>
          <button onClick={() => setReorderMessage(null)} className="ml-3 flex-shrink-0 opacity-60 hover:opacity-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </motion.div>
      )}

      {orders.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
            <svg className="w-8 h-8 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-cream mb-2">No orders yet</h3>
          <p className="text-text-muted">
            Your wholesale orders will appear here once you place your first order.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const isExpanded = expandedOrder === order.id;
            const status = statusConfig[order.status] || { label: order.status, className: 'bg-white/5 text-text-muted' };
            const itemCount = order.lineItems.reduce((sum, li) => sum + li.quantity, 0);

            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
              >
                {/* Order Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-display text-cream tracking-wide">
                          {order.name}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${status.className}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-sm text-text-muted mt-0.5">
                        {new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                        {' '}&middot; {itemCount} item{itemCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-text-muted uppercase tracking-wide">Total</p>
                      <p className="text-lg font-semibold text-accent">
                        ${parseFloat(order.totalPrice).toFixed(2)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleReorder(order)}
                      disabled={reorderingId === order.id}
                      className="px-3 py-1.5 text-xs uppercase tracking-wide border border-white/10 text-cream rounded-sm hover:bg-white/5 transition-colors font-medium disabled:opacity-50 disabled:cursor-wait"
                    >
                      {reorderingId === order.id ? 'Loading...' : 'Reorder'}
                    </button>
                    {order.invoiceUrl && (
                      <a
                        href={order.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-xs uppercase tracking-wide border border-accent text-accent rounded-sm hover:bg-accent hover:text-workshop transition-colors font-medium"
                      >
                        Pay Invoice
                      </a>
                    )}
                  </div>
                </div>

                {/* Expand/Collapse Line Items */}
                <div className="mt-3 pt-3 border-t border-white/[0.06]">
                  <button
                    onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    className="text-xs text-accent hover:text-accent/80 uppercase tracking-wide font-medium"
                  >
                    {isExpanded ? 'Hide' : 'Show'} Items ({order.lineItems.length})
                  </button>

                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-3 bg-white/5 rounded-sm p-3"
                    >
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-text-muted">
                            <th className="py-1 text-xs font-medium">Product</th>
                            <th className="py-1 text-xs font-medium hidden sm:table-cell">SKU</th>
                            <th className="py-1 text-xs font-medium text-center">Qty</th>
                            <th className="py-1 text-xs font-medium text-right">Price</th>
                            <th className="py-1 text-xs font-medium text-right">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.lineItems.map((item, idx) => (
                            <tr key={idx} className="border-t border-white/[0.06]">
                              <td className="py-1.5 text-cream">{item.title}</td>
                              <td className="py-1.5 font-mono text-text-muted hidden sm:table-cell">
                                {item.sku || '-'}
                              </td>
                              <td className="py-1.5 text-center text-cream">{item.quantity}</td>
                              <td className="py-1.5 text-right font-mono text-cream">
                                ${parseFloat(item.discountedPrice).toFixed(2)}
                              </td>
                              <td className="py-1.5 text-right font-mono font-medium text-cream">
                                ${(parseFloat(item.discountedPrice) * item.quantity).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* Load More */}
          {hasMore && (
            <div className="text-center pt-4">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-6 py-2 border border-white/10 text-cream rounded-sm hover:bg-white/5 transition-colors uppercase tracking-wide text-sm font-medium disabled:opacity-50"
              >
                {loadingMore ? 'Loading...' : 'Load More Orders'}
              </button>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
